import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { pluralizeWithCount } from "@common/helpers/string";
import { ServiceTemplateScheduleRow } from "../serviceTemplateSchedulesViewConfig";
import {
    ServiceTemplateScheduleCallbacks,
    ServiceTemplateScheduleTableOptions,
} from "../ServiceTemplateSchedulesList";
import styles from "./ServiceTemplateScheduleEntityCell.module.scss";

export const ServiceTemplateScheduleEntityCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ServiceTemplateScheduleRow, string>) => {
        const meta = useViewTableMeta<
            ServiceTemplateScheduleCallbacks,
            ServiceTemplateScheduleTableOptions
        >(table);
        const entityInfoByUuid = meta.options?.entityInfoByUuid;

        // Group rows (schedules sharing a groupId) show the entity icons of
        // every member schedule as a preview; the individual entity + name are
        // shown on the expanded leaf rows below.
        const groupEntities = useMemo(() => {
            if (row.original.rowType !== "group") {
                return [];
            }
            return row.original.children.flatMap((child) => {
                const info = entityInfoByUuid?.get(child.entityUuid);
                return info ? [info] : [];
            });
        }, [row.original, entityInfoByUuid]);

        if (row.original.rowType === "group") {
            return (
                <ViewTableCell>
                    <EntityAvatarGroup
                        entities={groupEntities}
                        max={3}
                        size="xs"
                        label={
                            <span className="text-secondary">
                                {pluralizeWithCount(
                                    "entity",
                                    groupEntities.length,
                                )}
                            </span>
                        }
                    />
                </ViewTableCell>
            );
        }

        const { entityUuid } = row.original;
        const entityInfo = entityUuid
            ? entityInfoByUuid?.get(entityUuid)
            : undefined;
        const entityName = entityInfo?.name ?? "-";

        return (
            <ViewTableCell>
                <div className={styles.container}>
                    {entityInfo && (
                        <EntityAvatar
                            size="xs"
                            entity={entityInfo}
                            aria-hidden
                        />
                    )}
                    <span className="text-secondary">{entityName}</span>
                </div>
            </ViewTableCell>
        );
    },
);

ServiceTemplateScheduleEntityCell.displayName =
    "ServiceTemplateScheduleEntityCell";
